import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Popconfirm,
  Modal,
  Tooltip,
  Typography,
  Tag,
  Row,
  Col,
  Card,
  Spin,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { holidayService } from '../../services/holidayService';
import getApiErrorMessage from '../../utils/getApiErrorMessage';
import HolidayForm from './HolidayForm';
import ViewModeSelect from '../common/ViewModeSelect';
import useResponsiveTableView from '../../hooks/useResponsiveTableView';

const { Title, Text } = Typography;

const HOLIDAY_ICON_COLOR = '#FF0000';
// Sum of the column min-widths; below this the table can't be shown in full → cards.
const MIN_TABLE_WIDTH = 720;

const HolidayTable = () => {
  const { t, i18n } = useTranslation();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [manualView, setManualView] = useState('auto');
  const { containerRef, view } = useResponsiveTableView({ minTableWidth: MIN_TABLE_WIDTH, manualView });

  const currentYear = new Date().getFullYear();

  const formatHolidayDate = (date) => {
    const locale = i18n.language === 'ro' ? 'ro-RO' : 'en-GB';
    return new Date(date).toLocaleDateString(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  useEffect(() => {
    fetchHolidays();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHolidays = async () => {
    try {
      const data = await holidayService.getAll(currentYear);
      setHolidays(data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      message.error(t('employee.holiday.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingHoliday(null);
    setFormOpen(true);
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormOpen(true);
  };

  const handleDelete = async (holiday) => {
    try {
      await holidayService.delete(holiday.id);
      message.success(t('employee.holiday.deleteSuccess'));
      fetchHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      message.error(getApiErrorMessage(error, 'employee.holiday.deleteError', t));
    }
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (editingHoliday) {
        await holidayService.update(editingHoliday.id, formData);
        message.success(t('employee.holiday.updateSuccess'));
      } else {
        await holidayService.create(formData);
        message.success(t('employee.holiday.createSuccess'));
      }
      setFormOpen(false);
      fetchHolidays();
    } catch (error) {
      console.error('Error saving holiday:', error);
      message.error(getApiErrorMessage(error, 'employee.holiday.saveError', t));
    } finally {
      setFormLoading(false);
    }
  };

  const columns = [
    {
      title: t('employee.holiday.columns.holiday'),
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <CalendarOutlined style={{ color: HOLIDAY_ICON_COLOR }} />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: t('employee.holiday.columns.date'),
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      defaultSortOrder: 'ascend',
      render: (date) => <Tag color="red">{formatHolidayDate(date)}</Tag>,
    },
    {
      title: t('employee.holiday.columns.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: t('employee.holiday.columns.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              data-testid="holiday-action-edit"
            />
          </Tooltip>
          <Popconfirm
            title={t('employee.holiday.deleteTitle')}
            description={t('employee.holiday.deleteConfirm', { name: record.name })}
            onConfirm={() => handleDelete(record)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('common.delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                data-testid="holiday-action-delete"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderCard = (holiday) => (
    <Card
      hoverable
      data-testid="holiday-card"
      actions={[
        <Tooltip key="edit" title={t('common.edit')}>
          <EditOutlined
            className="touch-button"
            onClick={() => handleEdit(holiday)}
            data-testid="holiday-action-edit"
          />
        </Tooltip>,
        <Popconfirm
          key="delete"
          title={t('employee.holiday.deleteTitle')}
          description={t('employee.holiday.deleteConfirm', { name: holiday.name })}
          onConfirm={() => handleDelete(holiday)}
          okText={t('common.yes')}
          cancelText={t('common.no')}
        >
          <Tooltip title={t('common.delete')}>
            <DeleteOutlined
              className="touch-button"
              style={{ color: HOLIDAY_ICON_COLOR }}
              data-testid="holiday-action-delete"
            />
          </Tooltip>
        </Popconfirm>,
      ]}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space align="start">
          <CalendarOutlined style={{ color: HOLIDAY_ICON_COLOR, fontSize: 20 }} />
          <Title level={5} style={{ margin: 0 }}>{holiday.name}</Title>
        </Space>
        <Tag color="red">{formatHolidayDate(holiday.date)}</Tag>
        {holiday.description && (
          <Text type="secondary">{holiday.description}</Text>
        )}
      </Space>
    </Card>
  );

  const renderCards = () => {
    if (!loading && holidays.length === 0) {
      return <Empty />;
    }
    return (
      <Row gutter={[16, 16]}>
        {holidays.map((holiday) => (
          <Col xs={24} sm={12} lg={8} xl={6} key={holiday.id}>
            {renderCard(holiday)}
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
        <Title level={4} style={{ margin: 0 }}>{t('employee.holiday.title', { year: currentYear })}</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          data-testid="add-holiday-button"
        >
          {t('employee.holiday.addHoliday')}
        </Button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <ViewModeSelect value={manualView} onChange={setManualView} data-testid="view-mode-select" />
      </div>

      <div ref={containerRef}>
        {view === 'table' ? (
          <Table
            columns={columns}
            dataSource={holidays}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 20,
              showTotal: (total) => t('employee.holiday.total', { total }),
            }}
          />
        ) : (
          <Spin spinning={loading}>{renderCards()}</Spin>
        )}
      </div>

      <Modal
        title={editingHoliday ? t('employee.holiday.editHoliday') : t('employee.holiday.addHoliday')}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        footer={null}
        width="min(500px, 95vw)"
        destroyOnClose
      >
        <HolidayForm
          onSubmit={handleFormSubmit}
          holiday={editingHoliday}
          loading={formLoading}
        />
      </Modal>
    </div>
  );
};

export default HolidayTable;
